import React, { FC, KeyboardEvent, MutableRefObject, ReactElement, useContext, useEffect, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import Table from '@material-ui/core/Table'
import { TableBody, TableCell, TableHead, TableRow } from '@material-ui/core'
import { DeviceModal } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import {
    DeviceOrAgentGroupMembershipRow,
    getAgentGroupMemberships,
    getDeviceGroupMemberships,
    getLinkedUsersForAgent,
    getLinkedUsersForDevice
} from '../../../NonReact/common/API/EntityAPIClient'
import { EMPTY_STRING } from '../../Common/Constants/Separators'
import MessageContext, { MessageContextType } from '../../Common/Contexts/MessageContext'
import { ReactState } from '../../Common/Types/Types'
import { IsVarEmpty, isStringNotEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'

export type LinkedEntitiesMode = 'device' | 'agent'

const LINKED_ENTITIES_SECTION_CLASS = 'linked-entities-section'
const ROUTER_DATATABLE_TABLE_CONTAINER_CLASS = 'router-datatable-table-container'
const ROUTER_DATATABLE_HEAD_CLASS = 'router-datatable-head'
const ROUTER_DATATABLE_BODY_CLASS = 'router-datatable-body'
const ROUTER_DATATABLE_BODY_DATA_CELL_CLASS = 'router-datatable-body-data-cell'

const filterLinkedGroupRows = (groupRows: DeviceOrAgentGroupMembershipRow[]): DeviceOrAgentGroupMembershipRow[] =>
    groupRows.filter(
        (groupRow: DeviceOrAgentGroupMembershipRow): boolean =>
            isStringNotEmpty(groupRow.ID) || isStringNotEmpty(groupRow.Description)
    )

const filterLinkedUserIDs = (userIds: string[]): string[] =>
    userIds.filter((userId: string): boolean => isStringNotEmpty(userId))

type Props = {
    mode: LinkedEntitiesMode
    filterTelSysID: number
    filterPartitionID: number
    /** Device number or agent number in partition. */
    entityNumber: string
    refreshNonce?: number
    onSelectGroupDevice?: (groupDeviceNumber: string) => void
    onSelectLinkedUser?: (userId: string) => void
}

/**
 * Read-only Linked Entities tab: group memberships and associated user IDs (device list {@code Users} rules).
 *
 * @param props - TelSys / partition scope and entity identifier.
 * @returns Linked-entity tables with navigate actions plus loading state.
 */
export const LinkedEntitiesTab: FC<Props> = ({
    mode,
    filterTelSysID,
    filterPartitionID,
    entityNumber,
    refreshNonce,
    onSelectGroupDevice,
    onSelectLinkedUser
}: Props): ReactElement => {
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const { displayError }: MessageContextType = useContext(MessageContext)

    const [groups, setGroups]: ReactState<DeviceOrAgentGroupMembershipRow[]> = useState<
        DeviceOrAgentGroupMembershipRow[]
    >([])
    const [users, setUsers]: ReactState<string[]> = useState<string[]>([])
    const [isLoading, setIsLoading]: ReactState<boolean> = useState<boolean>(true)

    useEffect((): void => {
        const load = async (): Promise<void> => {
            setIsLoading(true)

            try {
                if (mode === 'device') {
                    const [groupRows, userIds]: [DeviceOrAgentGroupMembershipRow[], string[]] = await Promise.all([
                        getDeviceGroupMemberships(filterTelSysID, filterPartitionID, entityNumber),
                        getLinkedUsersForDevice(filterTelSysID, filterPartitionID, entityNumber)
                    ])

                    if (isMounted.current) {
                        setGroups(filterLinkedGroupRows(groupRows))
                        setUsers(filterLinkedUserIDs(userIds))
                    }
                } else {
                    const [groupRows, userIds]: [DeviceOrAgentGroupMembershipRow[], string[]] = await Promise.all([
                        getAgentGroupMemberships(filterTelSysID, filterPartitionID, entityNumber),
                        getLinkedUsersForAgent(filterTelSysID, filterPartitionID, entityNumber)
                    ])

                    if (isMounted.current) {
                        setGroups(filterLinkedGroupRows(groupRows))
                        setUsers(filterLinkedUserIDs(userIds))
                    }
                }
            } catch (error: unknown) {
                if (isMounted.current) {
                    displayError(error instanceof Error ? error : new Error(String(error)))
                    setGroups([])
                    setUsers([])
                }
            } finally {
                if (isMounted.current) setIsLoading(false)
            }
        }

        load().catch(() => undefined)
    }, [entityNumber, filterPartitionID, filterTelSysID, mode, refreshNonce])

    if (isLoading) {
        return (
            <div className='linked-entities-tab linked-entities-tab--loading'>
                {language.get(DeviceModal.DeviceModal_LinkedEntities_Loading)}
            </div>
        )
    }

    const openGroupDeviceModal = (groupDeviceNumber: string): void => {
        if (groupDeviceNumber === EMPTY_STRING || IsVarEmpty(onSelectGroupDevice)) return

        onSelectGroupDevice(groupDeviceNumber)
    }

    const handleGroupRowKeyDown = (event: KeyboardEvent<HTMLTableRowElement>, groupDeviceNumber: string): void => {
        if (event.key !== 'Enter' && event.key !== ' ') return

        event.preventDefault()
        openGroupDeviceModal(groupDeviceNumber)
    }

    const openLinkedUserModal = (userId: string): void => {
        if (userId === EMPTY_STRING || IsVarEmpty(onSelectLinkedUser)) return

        onSelectLinkedUser(userId)
    }

    const handleUserRowKeyDown = (event: KeyboardEvent<HTMLTableRowElement>, userId: string): void => {
        if (event.key !== 'Enter' && event.key !== ' ') return

        event.preventDefault()
        openLinkedUserModal(userId)
    }

    return (
        <div className='linked-entities-tab tab-area'>
            <section
                className={LINKED_ENTITIES_SECTION_CLASS}
                aria-label={language.get(DeviceModal.DeviceModal_LinkedEntities_Section_Groups)}
            >
                <h4 className='linked-entities-section__title'>
                    {language.get(DeviceModal.DeviceModal_LinkedEntities_Section_Groups)}
                </h4>
                <div className='linked-entities-table-wrap router-datatable'>
                    <div className={ROUTER_DATATABLE_TABLE_CONTAINER_CLASS}>
                        <Table className='linked-entities-table rd-table' aria-label='linked entities groups table'>
                            <TableHead className={ROUTER_DATATABLE_HEAD_CLASS}>
                                <TableRow>
                                    <TableCell component='th' scope='col'>
                                        {language.get(DeviceModal.DeviceModal_LinkedEntities_Column_GroupId)}
                                    </TableCell>
                                    <TableCell component='th' scope='col'>
                                        {language.get(DeviceModal.DeviceModal_LinkedEntities_Column_Description)}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody className={ROUTER_DATATABLE_BODY_CLASS}>
                                {groups.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={2} className='linked-entities-table__empty'>
                                            {language.get(DeviceModal.DeviceModal_LinkedEntities_Empty)}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    groups.map((row: DeviceOrAgentGroupMembershipRow, index: number) => (
                                        <TableRow
                                            key={`g-${row.ID ?? 'row'}-${index}`}
                                            hover
                                            tabIndex={isVarNotEmpty(onSelectGroupDevice) ? 0 : -1}
                                            onClick={(): void => openGroupDeviceModal(row.ID ?? EMPTY_STRING)}
                                            onKeyDown={(event: KeyboardEvent<HTMLTableRowElement>): void =>
                                                handleGroupRowKeyDown(event, row.ID ?? EMPTY_STRING)
                                            }
                                        >
                                            <TableCell
                                                className={`${ROUTER_DATATABLE_BODY_DATA_CELL_CLASS} router-datatable-body-clickable`}
                                            >
                                                {row.ID ?? EMPTY_STRING}
                                            </TableCell>
                                            <TableCell
                                                className={`${ROUTER_DATATABLE_BODY_DATA_CELL_CLASS} router-datatable-body-clickable`}
                                            >
                                                {row.Description ?? EMPTY_STRING}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            </section>

            <section
                className={LINKED_ENTITIES_SECTION_CLASS}
                aria-label={language.get(DeviceModal.DeviceModal_LinkedEntities_Section_Users)}
            >
                <h4 className='linked-entities-section__title'>
                    {language.get(DeviceModal.DeviceModal_LinkedEntities_Section_Users)}
                </h4>
                <div className='linked-entities-table-wrap router-datatable'>
                    <div className={ROUTER_DATATABLE_TABLE_CONTAINER_CLASS}>
                        <Table className='linked-entities-table rd-table' aria-label='linked entities users table'>
                            <TableHead className={ROUTER_DATATABLE_HEAD_CLASS}>
                                <TableRow>
                                    <TableCell component='th' scope='col'>
                                        {language.get(DeviceModal.DeviceModal_LinkedEntities_Column_UserId)}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody className={ROUTER_DATATABLE_BODY_CLASS}>
                                {users.length === 0 ? (
                                    <TableRow>
                                        <TableCell className='linked-entities-table__empty'>
                                            {language.get(DeviceModal.DeviceModal_LinkedEntities_Empty)}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    users.map((userId: string, index: number) => (
                                        <TableRow
                                            key={`u-${userId}-${index}`}
                                            hover
                                            tabIndex={isVarNotEmpty(onSelectLinkedUser) ? 0 : -1}
                                            onClick={(): void => openLinkedUserModal(userId)}
                                            onKeyDown={(event: KeyboardEvent<HTMLTableRowElement>): void =>
                                                handleUserRowKeyDown(event, userId)
                                            }
                                        >
                                            <TableCell
                                                className={`${ROUTER_DATATABLE_BODY_DATA_CELL_CLASS} router-datatable-body-clickable`}
                                            >
                                                {userId}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            </section>
        </div>
    )
}
